import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@redngapps/shared/env/types';
import { isFeatureSwitch } from '@redngapps/shared/env/util';
import { map, Observable } from 'rxjs';

import { IFeatureFlagDto } from './feature-flag.dto';

// Reverse-proxied backend prefix (see proxy.conf.json / nginx) → feature-flag-service.
const FLAGS_PATH = '/service/flags';

/**
 * The fields of the service's `GET /flags` response that the overview needs.
 */
interface IServerFlag {
  key: string;
  enabled: boolean;
}

/**
 * Reads and updates the runtime feature-flag state of the current instance against the
 * feature-flag-service (`GET /flags`, `PATCH /flags/:key`). The overview builds its
 * rows from the shared `FEATURE_FLAG_CATALOG` overlaid with this state.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsApiService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(ENVIRONMENT);

  getFlags(): Observable<IFeatureFlagDto[]> {
    return this.http.get<IServerFlag[]>(FLAGS_PATH).pipe(
      map(flags =>
        flags.map(
          (flag): IFeatureFlagDto => ({
            key: flag.key,
            persistedEnabled: flag.enabled,
          }),
        ),
      ),
    );
  }

  /**
   * Whether the feature is released in this instance's environment, from `featureSwitches`. Purely
   * env-derived, so it holds for every catalog key regardless of persistence (unknown or disabled
   * keys count as not released).
   */
  isEnvReleased(key: string): boolean {
    return isFeatureSwitch(key) && this.environment.featureSwitches[key] === true;
  }

  /**
   * Switches a flag and optionally records why. `reason` is a per-toggle justification
   * stored on the audit entry (not on the flag); it is only sent when non-empty, matching
   * the service's optional `reason` field.
   */
  setFlag(key: string, enabled: boolean, reason?: string): Observable<void> {
    return this.http
      .patch(`${FLAGS_PATH}/${encodeURIComponent(key)}`, { enabled, ...(reason ? { reason } : {}) })
      .pipe(map(() => undefined));
  }

  /**
   * Removes a flag's database entry. Its own route rather than `setFlag(key, true)`: both
   * write the same state, but only `DELETE` is logged as a removal. The body is only sent
   * when a reason was given.
   */
  removeFlag(key: string, reason?: string): Observable<void> {
    return this.http
      .delete(`${FLAGS_PATH}/${encodeURIComponent(key)}`, reason ? { body: { reason } } : undefined)
      .pipe(map(() => undefined));
  }
}
